import get from "lodash/get";

import removeHTMLTags from "../helper/removeHTMLTags";
import { getddMMyyyy, gethhmm, handleTimeFormat } from "../helper/date-utils";
import { getMemberSubscriptionInfo } from "../helper/api";

import { PAGE_TYPE } from "../../constants";

export const getHeadline = (story) => {
  if (!story) {
    return null;
  }

  return story.headline || get(story, ["alternative", "home", "default", "headline"], null);
};

export const getSectionName = (story) => {
  if (!story) {
    return null;
  }

  return get(story, ["sections", "0", "display-name"]) || get(story, ["sections", "0", "name"]);
};

export const getSectionSlug = (story) => get(story, ["sections", "0", "slug"], "#");

export const getAuthor = (story) => {
  if (!story) {
    return null;
  }

  return get(story, ["authors", "0"], {});
};

export const getAuthors = (story) => {
  const authorList = story.authors || [];

  if (!authorList.length) {
    return authorList; // return empty []
  }

  return authorList.map((c) => ({
    name: c.name,
    slug: `/author/${c.id}/${c.slug}`,
  }));
};

export const getUserMetaData = (member) => get(member, ["metadata", "userData"], {});

export const isMobileNoPresent = (member) => {
  const mobile = get(member, ["metadata", "userData", "mobileNumber"], null);

  return !!mobile;
};

export const getMobileNubmber = (member) => get(member, ["metadata", "userData", "mobileNumber"], null);

export const isAuthorNameShown = (story) => {
  const authorBlockList = get(story, ["metadata", "story-attributes", "authorblock"], null);

  // If authorblock contains a value as "show" then show the author name
  return !!(authorBlockList && authorBlockList.length && authorBlockList.includes("show"));
};

export const getStoryLine = (story) => {
  if (!story) {
    return null;
  }

  const value = get(story, ["metadata", "sponsored-by"], null);
  return value;
};

export const getTitleAndCards = (cards) => {
  const newCards = [];

  cards.forEach((card) => {
    const titleElement = card["story-elements"].find(({ type }) => type === "title");

    if (titleElement) {
      const elements = [...card["story-elements"]];
      const index = elements.findIndex((i) => i.id === titleElement.id);
      if (index !== -1) elements.splice(index, 1); // remove title from elements

      newCards.push({
        title: titleElement,
        card: {
          ...card,
          "story-elements": elements,
        },
      });
    }
  });
  return newCards;
};

export const authors = (page, story) => {
  if (page.type === "author" && page.author_name) {
    return page.author_name;
  }

  if (story) {
    if (story.authors) {
      return story.authors.map((i) => i.name).join(",");
    } else if (story.author && story.author.name) {
      return story.author.name;
    } else if (story["author-name"]) {
      return story["author-name"];
    } else {
      return "";
    }
  }
};

export const getLanguage = (publisher) => {
  switch (publisher) {
    case "quint":
      return "en";
    case "quint-hindi":
      return "hi";
    default:
      return "en";
  }
};

export const sections = (page, story) => {
  switch (page.type) {
    case "home":
      return "HOME";
    case "section":
      return page.section_name;
    case "story":
      return story.sections.map((section) => section.name).join(",");
    default:
      return "";
  }
};

// UTMS may be added in multiple pages. Re-use below fn() if any one is doing so.
export const getUtm = (name, publisher) => {
  const prefixes = {
    quint: "",
    "quint-hindi": "hq_",
    fit: "fit_",
  };
  return `${prefixes[publisher]}${name}`;
};

// Meant for schemas
export const getStoryWithConfig = (state) => {
  return {
    story: get(state, ["qt", "data", "story"], {}),
    config: get(state, ["qt", "config"], {}),
  };
};

// Meant for Rendering ClaimReview Schema
export const hasWebqoofSection = (sections) => {
  return sections.map((ele) => ele.slug).includes("webqoof") || sections.map((ele) => ele.slug).includes("fit-webqoof");
};

export const getCollectionSlug = (collection, allSections, isHomePage = false) => {
  let slug = "";

  // Check if section id exists in metadata which indicates navigation should happen to section page
  const sectionId = get(collection, ["metadata", "section", "0", "id"], null);

  if (sectionId) {
    // Fetch the Slug of section by searching in allSections array
    const section = allSections.find((c) => c.id === sectionId);

    if (section && section["parent-id"]) {
      const parentSection = allSections.find((item) => item.id === section["parent-id"]);

      if (parentSection) {
        slug = `/${parentSection.slug}/${section.slug}`;
      }
    } else if (section && isHomePage) {
      slug = section.slug;
    } else {
      slug = `/collection/${collection.slug}`;
    }
  } else {
    slug = `/collection/${collection.slug}`;
  }

  return slug;
};

export const resizeIframes = () => {
  const iframes = document.querySelectorAll(".story-element-jsembed iframe");
  if (iframes.length) {
    iframes.forEach((i) => {
      window.iFrameResize({ checkOrigin: false }, i);
    });
  }
};

export const isMobileWeb = () => {
  const ua = (window.navigator.userAgent || window.navigator.vendor || window.opera).toString().toLowerCase();
  return /(android|ipad|iphone)/.test(ua) ? "mWEB" : "WEB";
};

export const getArticleBodyFromStory = (story) => {
  let accumulator = "";

  story.cards.forEach((card) => {
    card["story-elements"].forEach((storyElement) => {
      if (storyElement.type === "text") {
        accumulator += removeHTMLTags(storyElement.text);
      }
    });
  });

  return accumulator;
};

export const getSeobySectionId = (sectionId, config) => {
  return get(config, ["seo-metadata"], []).filter((seoMetadata) => seoMetadata["owner-id"] === sectionId);
};

export const getPageTitle = (data, page, config) => {
  if (page === PAGE_TYPE.STORY_PAGE) {
    return get(data, ["story", "seo", "meta-title"], data.title);
  } else if (page === PAGE_TYPE.SECTION_PAGE) {
    const filteredSeoConfig = getSeobySectionId(get(data, ["sectionId"]), config);
    const altTitle = get(config, ["publisher-settings", "title"], "The Quint");

    return get(filteredSeoConfig, [0, "data", "page-title"], altTitle);
  } else if (page === PAGE_TYPE.AUTHOR_PAGE) {
    const author = get(data, ["author", "name"], "");

    return `${author} on The Quint`;
  }
};

export const getNamesFromArray = (list = []) => {
  return list.map((author) => author.name).join(", ");
};

export const getAgency = (story = {}) => {
  return get(story, ["metadata", "story-attributes", "syndicatedfrom", "0"]);
};

export const getVideoEmbedId = (story = {}) => {
  const embedIds = [];

  story.cards.forEach((cardItem) => {
    cardItem["story-elements"].forEach((element) => {
      if (element.type === "youtube-video") {
        embedIds.push(`${element["embed-url"].split("/").slice(-1)}`);
      } else if (element.subtype === "dailymotion-video") {
        embedIds.push(`${get(element, ["metadata", "video-id"], "")}`);
      }
    });
  });

  return embedIds.length ? `Yes - ${embedIds}` : "";
};

export const getSectionAndSubsectionBySlug = (slug) => {
  if (!slug) {
    return;
  }

  slug = slug.split("/");
  slug.pop();
  return slug;
};

export const getPushNotificationStatus = () => {
  const Notification = window.Notification || window.mozNotification || window.webkitNotification;

  if (Notification) {
    return Notification.permission === "granted" ? "Notification - Yes" : "No";
  } else {
    console.log("Notification API not supported!");
    return "No";
  }
};

export const getSubscriptionInfo = (userGTMs) => {
  if (window.app.m_status) {
    userGTMs.user_type = "Paid";
    userGTMs.m_status = window.app.m_status;
    userGTMs.connect_level = getPushNotificationStatus();
    window.localStorage.setItem("USER:GTM", JSON.stringify(userGTMs));
    window.dataLayer.push(userGTMs);
  } else {
    getMemberSubscriptionInfo().then((response) => {
      if (response.subscriptions && response.subscriptions.length) {
        // This IF STATEMENT DENOTES that whether a Paid User,Or if user is having a plan
        window.app.m_status = response.subscriptions[0].plan_description;
        userGTMs.user_type = "Paid";
        userGTMs.m_status = `${response.subscriptions[0].plan_description}`;
        userGTMs.connect_level = getPushNotificationStatus();
        window.localStorage.setItem("USER:GTM", JSON.stringify(userGTMs));
        window.dataLayer.push(userGTMs);
      } else {
        // Comes here for Non Paid User
        userGTMs.connect_level = getPushNotificationStatus();
        window.localStorage.setItem("USER:GTM", JSON.stringify(userGTMs));
        window.dataLayer.push(userGTMs);
      }
    });
  }
};

function addGTMUserFields() {
  const userFields = {
    userid: "",
    user_type: "Non Paid",
    login: "No",
    m_status: "",
    connect_level: getPushNotificationStatus(),
    frequency: "",
  };

  let parsedUserFields;
  const userFieldsFromStorage = window.localStorage.getItem("USER:GTM");

  if (userFieldsFromStorage === "undefined") {
    parsedUserFields = Object.assign({}, userFields);
  } else {
    parsedUserFields = JSON.parse(userFieldsFromStorage);
  }

  window.localStorage.setItem(
    "USER:GTM",
    JSON.stringify({
      ...parsedUserFields,
      ...userFields,
    })
  );
  window.dataLayer.push({
    ...parsedUserFields,
    ...userFields,
  });
}

/**
 * Page argument will be for home,section,topic,search,plan selection in order to update only few fields
 * Story Page:-- addGTMPageVariables(storyObj,"WEB","storyIndex",null)
 * Home Page:-- addGTMPageVariables(null,"WEB",null,"HOME")
 * Section Page:-- addGTMPageVariables(null,"WEB",null,"SECTION")
 * AMP Story Page:-- addGTMPageVariables(storyObj,"AMP",null,null)
 */

export const addGTMPageVariables = (story = {}, platform, storyIndex, page) => {
  if (platform !== "AMP") {
    addGTMUserFields();
    if (window && window.matchMedia("(display-mode: standalone)").matches) {
      platform = "PWA";
    }
  }
  const defaultParams = {
    page_title: "",
    page_tp: "",
    authors: "",
    section: "",
    subsection: "",
    cmsid: "",
    agency: "The Quint",
    special_coverage: "",
    video_embed: "",
    audio_embed: "",
    nature_of_content: "",
    meta_keywords: "",
    published_date: "",
    published_time: "",
    last_update_date: "",
    platform,
    dark_mode:
      platform === "AMP" ? "No" : document.documentElement.getAttribute("theme").includes("dark") ? "Yes" : "No",
    story_index: "",
  };

  if (page) {
    const path = window.location.pathname;
    const [listingSection, listingSubSection] = path.substr(1).split("/");

    window.dataLayer.push({
      ...defaultParams,
      page_tp: page === "HOME" ? "Listing-Home" : "Listing-Section",
      page_title: document.title,
      cmsid: page === "HOME" ? "HOME" : path,
      section: listingSection || page,
      subsection: listingSubSection || "",
      authors: "The Quint",
    });
  } else if (story) {
    const isSyndicatedStory = get(story, ["story-template"]) === "syndicated";
    const audioEmbed = get(story, ["story-audio", "s3-key"], "");
    const storyTemplate = get(story, ["story-template"]);
    const [section, ...subsections] = story.slug ? getSectionAndSubsectionBySlug(story.slug) : "";
    const agency = get(story, ["metadata", "story-attributes", "syndicatedfrom", "0"]);

    const object = {
      ...defaultParams,
      ...{
        page_title: `${get(story, ["seo", "meta-title"], story.headline)}`,
        page_tp: `${storyTemplate === null ? "text" : storyTemplate}`,
        authors: getNamesFromArray(get(story, ["authors"], [])),
        section: `${section}`,
        subsection: `${subsections}`,
        cmsid: `${story.id}`,
        agency: `${isSyndicatedStory ? agency : "The Quint"}`,
        special_coverage: "",
        video_embed: `${getVideoEmbedId(story) || `No`}`,
        audio_embed: audioEmbed !== null && audioEmbed !== "" ? `Yes - ${audioEmbed}` : `No`,
        meta_keywords: `${story.seo["meta-keywords"]}`,
        published_date: `${getddMMyyyy(story["first-published-at"]).replace(",", "")}`,
        published_time: gethhmm(story["first-published-at"]),
        last_update_date: `${handleTimeFormat(story["published-at"])}`,
        story_index: platform === "AMP" ? "" : storyIndex,
      },
    };
    if (platform === "AMP") {
      return object;
    } else {
      window.dataLayer.push(object);
    }
  }
};

export const setTimeOnClient = () => {
  // In some cases we have time showing in span elements,
  // so querying both the cases, if any span having the class of *published_only* in them
  document.querySelectorAll("time,span[class*='published_only']").forEach((ele) => {
    let timestamp;

    if (ele.getAttribute("datetime")) {
      timestamp = new Date(ele.getAttribute("datetime")).getTime();
    } else if (ele.getAttribute("data-time")) {
      timestamp = new Date(ele.getAttribute("data-time")).getTime();
    }

    ele.innerText = handleTimeFormat(timestamp);
  });
};
